import React, { useEffect, useRef } from "react";
import './stepCompetencesEvaluation.css';
import StepCompetencesEvaluationDesc from "../stepCompetencesEvaluationDesc/stepCompetencesEvaluationDesc";
import FormEvaluationAnswerGroupEv from "../formEvaluationAnswerGroup/formEvaluationAnswerGroupEv";

import { Layout, Typography, Button, Input, message } from "antd";
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const StepCompetencesEvaluation = ({ 
    questions, 
    evaluation, 
    dataEvaluationAnswerGroups,
    onAnswerChange,
    onObservationChange,
    saveEvaluationAnswers,
    currentStep = 0,
    onStepChange,
    isSavingEvaluation = false
}) => {
    const topRef = useRef(null);
    const questionRefs = useRef({});
    const previousStepRef = useRef(currentStep);
    const evaluationServiceId = Number(evaluation?.evl_srv_id);
    const isIashEvaluation = evaluationServiceId === 6;
    const questionDisplayMode = `${evaluation?.evl_question_display_mode || (isIashEvaluation ? 'random' : 'grouped')}`;
    const isRandomizedEvaluation = questionDisplayMode === 'random';
    const effectiveCommentsOption = isIashEvaluation ? '0' : `${evaluation?.evl_comments_option ?? '0'}`;

    const totalSteps = questions.length + 2; // Bienvenida + Final
    const isLastStep = currentStep === totalSteps - 1;

    const formatWithLineBreaks = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, index) => (
            <React.Fragment key={index}>
                {line}
                <br />
            </React.Fragment>
        ));
    };

    const focusQuestionRef = (questionRef) => {
        if (!questionRef || !questionRef.scrollIntoView) {
            return;
        }

        questionRef.scrollIntoView({ behavior: "smooth", block: "center" });
        questionRef.classList.add("highlight-question");

        const focusableElement = questionRef.querySelector('input:not([type="hidden"]), textarea, button, [tabindex]:not([tabindex="-1"])');
        focusableElement?.focus?.({ preventScroll: true });

        setTimeout(() => {
            questionRef.classList.remove("highlight-question");
        }, 3000);
    };

    const focusFirstPendingQuestion = (stepToInspect = currentStep) => {
        if (stepToInspect <= 0 || stepToInspect >= totalSteps - 1) {
            return false;
        }

        const block = evaluation?.evr_questions?.[stepToInspect - 1];
        if (!block || !block.questions) {
            return false;
        }

        const pendingQuestion = block.questions.find((question) => {
            const hasAnswer = question.ean_answer !== null && question.ean_answer !== undefined && question.ean_answer !== "";
            const hasText = question.ean_answer_text !== null && question.ean_answer_text !== undefined && question.ean_answer_text !== "";
            return !hasAnswer && !hasText;
        });

        if (!pendingQuestion) {
            return false;
        }

        const questionRef = questionRefs.current[pendingQuestion.ean_dsc_id];
        focusQuestionRef(questionRef);
        return true;
    };

    useEffect(() => {
        const previousStep = previousStepRef.current;
        const movedForward = currentStep > previousStep;

        if (movedForward) {
            focusFirstPendingQuestion(currentStep);
        }

        previousStepRef.current = currentStep;
    }, [currentStep, evaluation]);

    if (!evaluation) {
        return null;
    }

    const renderStepContent = () => {
        if (currentStep === 0) {
            return (
                <div className="evaluation-welcome-block">
                    <Title level={3} className="evaluation-welcome-title">{evaluation.evl_title.toUpperCase()}</Title>
                    <Title level={4} className="evaluation-welcome-subtitle">
                        {evaluation.evr_evaluator_gender === "F" ? "BIENVENIDA" : "BIENVENIDO"} {evaluation.evr_evaluator_fullname.toUpperCase()}
                    </Title>
                    <Paragraph className="evaluation-welcome-text">
                        {evaluation.evl_welcome_message}
                    </Paragraph>
                    <div className="evaluation-welcome-info-card">
                        <StepCompetencesEvaluationDesc
                            evr_evaluator={evaluation.evr_evaluator}
                            evr_evaluator_fullname={evaluation.evr_evaluator_fullname}
                            evr_evaluator_job_title={evaluation.evr_evaluator_job_title}
                            evr_evaluated={evaluation.evr_evaluated}
                            evr_evaluated_fullname={evaluation.evr_evaluated_fullname}
                            evr_evaluated_job_title={evaluation.evr_evaluated_job_title}
                            evr_type={evaluation.evr_type}
                            evr_srv_id={evaluation.evl_srv_id}
                        />
                    </div>
                </div>
            );
        }
        
        if (currentStep === totalSteps - 1) {
            const generalObservation = evaluation?.evr_observations?.find(
                (o) => o.eob_evl_id === evaluation.evl_id && o.eob_cmp_id === null && o.eob_dsc_id === null
            )?.eob_observation || "";

            return (
                <div style={{ textAlign: "center" }}>
                    <div
                        style={{
                            marginBottom: 20,
                            padding: 16,
                            borderRadius: 10,
                            backgroundColor: "#fff7e6",
                            border: "1px solid #ffd591",
                            textAlign: "left"
                        }}
                    >
                        <Text style={{ fontWeight: 600, fontSize: '16px', color: '#ad6800' }}>
                            Tu progreso está en 99%
                        </Text>
                        <Paragraph style={{ margin: '8px 0 0', fontSize: '16px' }}>
                            Para registrar tus respuestas y cerrar la evaluación, presiona el botón "Enviar Encuesta".
                        </Paragraph>
                    </div>
                    {effectiveCommentsOption === '3' && (
                        <div style={{ marginTop: 20, textAlign: "left" }}>
                            <Text style={{ fontWeight: 500, fontSize: '16px' }} type="secondary">Observaciones</Text>
                            <TextArea
                                rows={4}
                                placeholder="Utiliza este espacio para consignar tus observaciones"
                                value={generalObservation}
                                onChange={(e) => onObservationChange(evaluation.evl_id, null, null, e.target.value)}
                                style={{ marginTop: 10, fontSize: '16px' }}
                            />
                        </div>
                    )}
                    <Paragraph style={{ fontSize: '16px' }}>{evaluation.evl_final_message}</Paragraph>
                    <Paragraph style={{ fontSize: '16px' }}>No olvides dar click en el botón "Enviar Encuesta" para cerrar la evaluación.</Paragraph>
                </div>
            );
        }

        const block = evaluation.evr_questions[currentStep - 1]; // Restamos 1 porque el paso 0 es la bienvenida
        const uniqueAnswerGroupIds = [...new Set(
            (block?.questions || [])
                .map((question) => question?.ean_eag_id)
                .filter((groupId) => groupId !== null && groupId !== undefined && groupId !== '')
        )];
        const blockEvaluationAnswerGroup = uniqueAnswerGroupIds.length === 1
            ? dataEvaluationAnswerGroups.find((group) => String(group?.eag_id) === String(uniqueAnswerGroupIds[0]))
            : null;
        let currentObservationBlock = evaluation.evr_observations.find(
                o => o.eob_evl_id === evaluation.evl_id &&
                    o.eob_cmp_id === block.cmp_id &&
                    o.eob_dsc_id === null
            )?.eob_observation || ""

        // Validar si mostrar el tipo de atributo cultural
        const shouldShowCultureType = evaluation.evl_srv_id === 3 && 
            block.questions && 
            block.questions[0] && 
            block.questions[0].ean_cat_type &&
            ["Atributo de Cultura", "Palancas culturales", "Compromiso"].includes(block.questions[0].ean_cat_type);

        return (
            <div>
                {currentStep === 1 && evaluation.evl_introductory_text && (
                    <div className="evaluation-intro-card">
                        <Paragraph className="evaluation-intro-text">
                            {evaluation.evl_introductory_text}
                        </Paragraph>
                    </div>
                )}
                {shouldShowCultureType && (
                    <div style={{ 
                        marginBottom: 16, 
                        padding: 12, 
                        backgroundColor: "#f0f8ff", 
                        borderLeft: "4px solid #1890ff", 
                        borderRadius: 4 
                    }}>
                        <Text style={{ 
                            fontWeight: 600, 
                            color: "#1890ff", 
                            fontSize: '16px',
                            textTransform: 'uppercase'
                        }}>
                            {block.questions[0].ean_cat_type}
                        </Text>
                    </div>
                )}
                {!isRandomizedEvaluation && <Title level={4}>{block.title}</Title>}
                {!isRandomizedEvaluation && blockEvaluationAnswerGroup?.eag_description && (
                    <Paragraph style={{ fontSize: '16px', marginTop: -8, marginBottom: 16 }}>
                        {formatWithLineBreaks(blockEvaluationAnswerGroup.eag_description)}
                    </Paragraph>
                )}
                {
                    block.questions.map((q, idx) => {
                        const evaluationAnswerGroup = dataEvaluationAnswerGroups.find(group => {
                            if (q?.ean_eag_id === null || q?.ean_eag_id === undefined) return false;
                            return String(group?.eag_id) === String(q?.ean_eag_id);
                        });
                        const questionNumber = Number.isInteger(q?.ean_order) ? q.ean_order + 1 : idx + 1;
                        
                        return (
                            <div 
                                ref={el => questionRefs.current[q.ean_dsc_id] = el}
                                key={idx} 
                                style={{ 
                                    marginBottom: 24, 
                                    padding: 16, 
                                    borderRadius: 10,
                            }}
                            >
                                <Text strong style={{ fontSize: '16px' }}>{`Pregunta ${questionNumber}`}</Text>

                                <div style={{ marginTop: 10 }}>
                                    <FormEvaluationAnswerGroupEv
                                        key={q.ean_dsc_id}
                                        source={block.source}
                                        evaluationAnswerGroup={evaluationAnswerGroup}
                                        question={q}
                                        evlCommentsOption={effectiveCommentsOption}
                                        onChange={(answer) => onAnswerChange(block.dimension, q.ean_dsc_id, answer)}
                                        handleObservationChange={(obsText) =>
                                            onObservationChange(evaluation.evl_id, q.ean_cmp_id, q.ean_dsc_id, obsText)
                                        }
                                        showDescription={isRandomizedEvaluation}
                                        currentObservation={
                                            evaluation.evr_observations.find(
                                                o => o.eob_evl_id === evaluation.evl_id &&
                                                    o.eob_cmp_id === q.ean_cmp_id &&
                                                    o.eob_dsc_id === q.ean_dsc_id
                                            )?.eob_observation || ""
                                        }
                                    />
                                </div>
                            </div>
                        )
                    })
                }
                {
                    block.source !== "evaluation" && effectiveCommentsOption === '2' && <div style={{ marginTop: 20 }}>
                        <Text style={{ fontWeight: 500 }} type="secondary">Observaciones</Text>
                        <TextArea
                            rows={4}
                            placeholder="Utiliza este espacio para consignar tus observaciones"
                            value={currentObservationBlock}
                            onChange={(e) => onObservationChange(evaluation.evl_id, block.cmp_id, null, e.target.value)}
                            style={{ marginTop: 10, fontSize: '16px' }}
                        />
                    </div>
                }
            </div>
        );
    };

    const handleNext = () => {
        if (isSavingEvaluation) {
            return;
        }

        if (!isCurrentStepValid()) {
            message.warning("Por favor responde todas las preguntas antes de continuar.");
            return;
        }

        if (currentStep < totalSteps - 1) {
            const nextStep = currentStep + 1;
            onStepChange?.(nextStep);
            scrollToTop();
            saveEvaluationAnswers(false, nextStep);
        }
        else {
            saveEvaluationAnswers(true, currentStep);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            onStepChange?.(currentStep - 1);
            scrollToTop();
        }
    };

    const scrollToTop = () => {
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // const isCurrentStepValid = () => {
    //     if (currentStep === 0 || currentStep === totalSteps - 1) return true;

    //     const block = evaluation.evr_questions[currentStep - 1];
    //     if (!block || !block.questions) return false;

    //     return block.questions.every(q => {
    //         const hasAnswer = q.ean_answer !== null && q.ean_answer !== undefined && q.ean_answer !== "";
    //         const hasText = q.ean_answer_text !== null && q.ean_answer_text !== undefined && q.ean_answer_text !== "";
    //         return hasAnswer || hasText;
    //     });

    //     // return true;
    // };

    const isCurrentStepValid = () => {
        if (currentStep === 0 || currentStep === totalSteps - 1) return true;

        const block = evaluation.evr_questions[currentStep - 1];
        if (!block || !block.questions) return false;

        for (let q of block.questions) {
            const hasAnswer = q.ean_answer !== null && q.ean_answer !== undefined && q.ean_answer !== "";
            const hasText = q.ean_answer_text !== null && q.ean_answer_text !== undefined && q.ean_answer_text !== "";

            if (!hasAnswer && !hasText) {
                const ref = questionRefs.current[q.ean_dsc_id];
                focusQuestionRef(ref);
                return false;
            }
        }

        return true;
    };

    return (
        <Layout 
        
            ref={topRef} 
            style={{
                background: "#fff",
                width: "100%",
                maxWidth: "850px", // o lo que prefieras
                padding: "24px 24px 32px",
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                position: "relative",
                marginTop: 8,
                marginBottom: 24,
            }}
        >
            <div style={{ marginTop: 0, marginBottom: currentStep === 0 ? 20 : 0, boxSizing: "border-box" }}>
                {renderStepContent()}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Button onClick={handlePrevious} disabled={currentStep === 0 || isSavingEvaluation}>
                    Anterior
                </Button>
                <Button type="primary" onClick={handleNext} loading={isSavingEvaluation} disabled={isSavingEvaluation}>
                    {isLastStep ? "Enviar Encuesta" : "Siguiente"}
                </Button>
            </div>
        </Layout>        
    );
};

export default StepCompetencesEvaluation;
